import apiClient from "./api";
import { getApiAccessToken } from "./UserService";

const baseUrl = "api";

export const TranslationService = {
  async getPages() {
    try {
      const token = await getApiAccessToken();
      const response = await apiClient.get(`${baseUrl}/translation/pages`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching pages:", error);
      throw error;
    }
  },

  async getContracts(userId: number) {
    try {
      const token = await getApiAccessToken();
      const response = await apiClient.get(
        `${baseUrl}/translation/contracts?userId=${userId || 1}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching contracts:", error);
      throw error;
    }
  },

  async getLanguages() {
    try {
      const token = await getApiAccessToken();
      const response = await apiClient.get(`${baseUrl}/translation/languages`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching languages:", error);
      throw error;
    }
  },

  /**
   * Bulk load ALL translations for ALL languages for an address system.
   * Returns { [languageId: number]: { [pageId: number]: Translation[] } } for client-side caching.
   */
  async getAllTranslations(addressSystemId: number) {
    try {
      const token = await getApiAccessToken();
      const response = await apiClient.get(
        `${baseUrl}/translation/all`,
        {
          params: {
            addressSystemId,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      // Return dictionary of languageId -> pageId -> translations
      return response.data as { [languageId: number]: { [pageId: number]: any[] } };
    } catch (error) {
      console.error("Error fetching all translations:", error);
      throw error;
    }
  },

  async getTranslations(pageId: number, addressSystemId: number, languageId: number) {
    try {
      const token = await getApiAccessToken();
      const response = await apiClient.get(
        `${baseUrl}/translation`,
        {
          params: {
            pageId,
            addressSystemId,
            languageId,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      // Return array of translations
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error("Error fetching translations:", error);
      throw error;
    }
  },

  async saveTranslations(
    translations: any[],
    pageId: number,
    addressSystemId: number,
    languageId: number
  ) {
    try {
      const payload = translations.map(t => ({ ...t, pageId }));
      //console.log("Saving translations:", payload);
      const token = await getApiAccessToken();
      const response = await apiClient.post(
        `${baseUrl}/translation?addressSystemId=${addressSystemId}&languageId=${languageId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error saving translations:", error);
      throw error;
    }
  },

  async createTranslation(
    pageId: number,
    addressSystemId: number,
    languageId: number,
    textCode: string,
    pageText: string
  ) {
    try {
      const token = await getApiAccessToken();
      const response = await apiClient.post(
        `${baseUrl}/translation/create`,
        {
          pageId,
          addressSystemId,
          languageId,
          textCode,
          pageText,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error creating translation:", error);
      throw error;
    }
  },

  async deleteTranslation(
    pageId: number,
    addressSystemId: number,
    languageId: number,
    textCode: string
  ) {
    try {
      const token = await getApiAccessToken();
      const response = await apiClient.delete(`${baseUrl}/translation/delete`, {
        params: {
          pageId,
          addressSystemId,
          languageId,
          textCode,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error deleting translation:", error);
      throw error;
    }
  },

  async getAddressSystems() {
    try {
      const token = await getApiAccessToken();
      const response = await apiClient.get(`${baseUrl}/translation/address-systems`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = response.data;
      return Array.isArray(data) ? data.map((s: any) => ({ ...s, name: s.name?.trim() ?? '' })) : data;
    } catch (error) {
      console.error("Error fetching address systems:", error);
      throw error;
    }
  },
};